import dayjs from 'dayjs';
import { BtnSecondary } from '@components/buttons';
import type { UnseenCheckinFeedback } from 'server/dbx/checkin-feedback';
import type { SummaryWindow } from 'shared/checkin-weeks';
import { useIntl } from 'shared/intl/use-intl';

/** "Barb replied to your Tuesday standup", on the course Home page. */
export function CoachFeedbackNotice({
  unseen,
  href,
}: {
  unseen: UnseenCheckinFeedback;
  href: string;
}) {
  const intl = useIntl();
  const latest = unseen.latest;
  if (!unseen.count || !latest) {
    return null;
  }
  const item =
    latest.itemKind === 'summary'
      ? intl('weekly summary')
      : intl('{day:string} standup', { day: dayjs(latest.itemDate).format('dddd') });
  const headline =
    latest.type === 'reply'
      ? intl('{coach:string} replied to your {item:string}', { coach: latest.coachName, item })
      : intl('{coach:string} reacted to your {item:string}', { coach: latest.coachName, item });
  const more = unseen.count - 1;

  return (
    <div
      role="status"
      class="flex flex-wrap items-center justify-between gap-3 border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-950 rounded-lg p-4"
    >
      <span class="text-gray-800 dark:text-gray-200">
        <strong>{headline}</strong>
        {more > 0 && (
          <span class="text-sm text-gray-600 dark:text-gray-400">
            {' '}
            {intl('and {count:number} more', { count: more })}
          </span>
        )}
      </span>
      <BtnSecondary href={href}>
        {latest.type === 'reply' ? intl('See reply') : intl('See it')}
      </BtnSecondary>
    </div>
  );
}

/**
 * On Home while this week's summary is open and not yet sent. The wording
 * tightens as the deadline gets close.
 */
export function SummaryDueNotice({
  window,
  localToday,
  isSent,
  href,
}: {
  window: SummaryWindow;
  localToday: string;
  isSent: boolean;
  href: string;
}) {
  const intl = useIntl();
  if (isSent) {
    return null;
  }
  const isLate = !!window.dueOn && localToday > window.dueOn;
  const isDueToday = window.dueOn === localToday;
  const headline = window.isSprint
    ? intl('Your Sprint summary is open. Send it before your coaching call.')
    : isLate
      ? intl(
          'Your weekly summary for week {week:number} is late. You can still send it until {closes:string}.',
          {
            week: window.weekNumber,
            closes: dayjs(window.closesOn).format('dddd'),
          },
        )
      : isDueToday
        ? intl('Your weekly summary for week {week:number} is due tonight.', {
            week: window.weekNumber,
          })
        : intl('Send your weekly summary for week {week:number} by the end of Sunday.', {
            week: window.weekNumber,
          });

  return (
    <div
      role="status"
      class={`flex flex-wrap items-center justify-between gap-3 border rounded-lg p-4 ${
        isLate || isDueToday
          ? 'border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950'
          : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'
      }`}
    >
      <strong class="text-gray-800 dark:text-gray-200">{headline}</strong>
      <BtnSecondary href={href}>{intl('Send summary')}</BtnSecondary>
    </div>
  );
}
